import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { convertDateToWords } from "../../helper/dateFormat";
import { slugify } from "../../helper/paramString";
import { Loader } from "../loader";
import { Unavailable } from "../noData";

const getNestedValue = (obj, path) => {
  if (!path || !obj) return "N/A";
  if (typeof path === "function") return path(obj) || "N/A";
  return path
    .split(".")
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : "N/A"), obj);
};

const renderStatus = (value) => {
  const statusStyles = {
    pending: "text-orange-500",
    processing: "text-blue-500",
    completed: "text-green-500",
    rejected: "text-red-500",
    cancelled: "text-gray-500",
  };
  return <span className={statusStyles[value] || "text-gray-500"}>{value || "N/A"}</span>;
};

export const CrudTable = ({ data, table, actions, id, isLoading, onUpdate }) => {
  const { id: paramId } = useParams();
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const handleEditClick = (row) => {
    setEditingId(row.id);
    // Initialize editData with all editable fields from the row
    const initialEditData = {};
    data.forEach(col => {
      if (col.edit) {
        initialEditData[col.value] = getNestedValue(row, col.value) || "";
      }
    });
    setEditData(initialEditData);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSave = async (rowId) => {
    try {
      await onUpdate(rowId, editData);
      setEditingId(null);
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const renderEditableField = (col, row) => {
    if (!col.edit) {
      return null;
    }

    switch (col.type) {
      case "status":
        return (
          <select
            name={col.value}
            value={editData[col.value] || ""}
            onChange={handleInputChange}
            className="ml-2 p-1 border rounded w-full"
          >
            {(col.options || []).map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      case "textarea":
        return (
          <textarea
            name={col.value}
            value={editData[col.value] || ""}
            onChange={handleInputChange}
            className="ml-2 p-1 border rounded w-full"
            rows="2"
          />
        );
      default:
        return (
          <input
            type={col.type || "text"}
            name={col.value}
            value={editData[col.value] || ""}
            onChange={handleInputChange}
            className="ml-2 p-1 border rounded w-full"
          />
        );
    }
  };

  const renderFieldValue = (col, row) => {
    const value = getNestedValue(row, col.value);
    
    switch (col.type) {
      case "status":
        return renderStatus(value);
      case "date":
        return <span className="text-[12px]">{convertDateToWords(value)}</span>;
      case "textarea":
        return <span className="text-[12px] whitespace-pre-line">{value || "N/A"}</span>;
      default:
        if (col.to) {
          return (
            <Link
              to={col.to
                ?.replace(`slugify({value})`, slugify(value))
                ?.replace("{id}", row.id)
                ?.replace("{paramId}", paramId)}
              className="text-orange-600 hover:italic max-w-full truncate"
            >
              {value}
            </Link>
          );
        }
        return <span className="text-[12px]">{value}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <Loader h="20" w="20" color="orange" />
      </div>
    );
  }

  return (
    <div className="w-full grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6 mb-2 px-5">
      <Unavailable data={table} className="text-black opacity-35 text-lg text-center py-2 col-span-3" />
      {(table || []).map((row) => (
        <div
          key={row.id}
          className="rounded-lg w-full h-max shadow-md hover:shadow-xl hover:scale-[1.01] transition-all ease-in-out duration-200"
        >
          <div className="flex flex-col justify-start items-start p-4">
            {data.map((col) => (
              <div key={col.key} className="text-sm font-light mb-1 w-full">
                <span className="font-medium">{col.key}:</span>{" "}
                {editingId === row.id && col.edit ? (
                  renderEditableField(col, row)
                ) : (
                  renderFieldValue(col, row)
                )}
              </div>
            ))}

            {actions?.length > 0 && (
              <div className="flex justify-center items-center gap-x-3 mt-3">
                {editingId === row.id ? (
                  <>
                    <button
                      onClick={() => handleSave(row.id)}
                      className="py-1 text-white bg-green-600 rounded-lg px-3 hover:bg-green-700 transition-all"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="py-1 text-white bg-gray-600 rounded-lg px-3 hover:bg-gray-700 transition-all"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {data.some(col => col.edit) && (
                      <button
                        onClick={() => handleEditClick(row)}
                        className="py-1 text-white bg-blue-600 rounded-lg px-3 hover:bg-blue-700 transition-all"
                      >
                        Edit
                      </button>
                    )}
                    {actions.map((action, index) =>
                      action.type === "delete" ? (
                        <button
                          key={index}
                          onClick={() => action.func(row.id)}
                          className={`py-1 text-red-600 border-2 rounded-lg px-2 border-red-600 hover:bg-red-600 hover:text-white transition-all ${
                            action.isLoading && id === row.id ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                          disabled={action.isLoading && id === row.id}
                        >
                          {action.isLoading && id === row.id ? (
                            <Loader w="5" h="5" color="red" />
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="1.5"
                              stroke="currentColor"
                              className="w-5 h-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                              />
                            </svg>
                          )}
                        </button>
                      ) : null
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};